import { useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { Close, SmartToy } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import ChartTypePicker from "./ChartTypePicker";
import AggregationConfig from "./AggregationConfig";
import WidgetFilterSection from "./WidgetFilterSection";
import WidgetPreview from "./WidgetPreview";
import {
  AggregationType,
  CHART_TYPES_BY_AGGREGATION,
  DEFAULT_WIDGET_BUILDER_VALUES,
  FIELDS_BY_AGGREGATION,
} from "./widgetBuilderConstants";
import {
  DashboardWidget,
  WidgetConfig,
  WIDGET_TYPES,
} from "../../types/dashboard/DashboardConfig";

dayjs.extend(utc);

export interface WidgetBuilderFormValues {
  title: string;
  chartType: string;
  aggregationType: AggregationType;
  field: string;
  interval: string;
  size: number;
  filterFrom: Dayjs | null;
  filterTo: Dayjs | null;
  filterAgentName: string;
  filterDirection: string | null;
  filterInteractionType: string | null;
  filterSkill: string;
  filterGroup: string;
  filterTags: string[];
}

const widgetToFormValues = (widget?: DashboardWidget): WidgetBuilderFormValues => {
  const config = widget?.config;
  const filters = config?.filters;
  return {
    title: widget?.title ?? DEFAULT_WIDGET_BUILDER_VALUES.title,
    chartType: config?.chartType ?? DEFAULT_WIDGET_BUILDER_VALUES.chartType,
    aggregationType: (config?.aggregationType ?? DEFAULT_WIDGET_BUILDER_VALUES.aggregationType) as AggregationType,
    field: config?.field ?? DEFAULT_WIDGET_BUILDER_VALUES.field,
    interval: config?.interval ?? DEFAULT_WIDGET_BUILDER_VALUES.interval,
    size: config?.size ?? DEFAULT_WIDGET_BUILDER_VALUES.size,
    filterFrom: filters?.from ? dayjs.utc(filters.from) : null,
    filterTo: filters?.to ? dayjs.utc(filters.to) : null,
    filterAgentName: filters?.agentName ?? "",
    filterDirection: filters?.direction ?? null,
    filterInteractionType: filters?.interactionType ?? null,
    filterSkill: filters?.skill ?? "",
    filterGroup: filters?.group ?? "",
    filterTags: filters?.tags ?? [],
  };
};

const formValuesToConfig = (values: WidgetBuilderFormValues): WidgetConfig => {
  const hasFilters =
    values.filterFrom ||
    values.filterTo ||
    values.filterAgentName ||
    values.filterDirection ||
    values.filterInteractionType ||
    values.filterSkill ||
    values.filterGroup ||
    values.filterTags.length > 0;

  return {
    chartType: values.chartType,
    aggregationType: values.aggregationType,
    field: values.aggregationType === "count" ? undefined : values.field,
    interval: values.aggregationType === "dateHistogram" ? values.interval : undefined,
    size: values.aggregationType === "terms" ? values.size : undefined,
    filters: hasFilters
      ? {
          from: values.filterFrom?.utc().toISOString() ?? undefined,
          to: values.filterTo?.utc().toISOString() ?? undefined,
          agentName: values.filterAgentName || undefined,
          direction: values.filterDirection || undefined,
          interactionType: values.filterInteractionType || undefined,
          skill: values.filterSkill || undefined,
          group: values.filterGroup || undefined,
          tags: values.filterTags.length > 0 ? values.filterTags : undefined,
        }
      : undefined,
  };
};

interface WidgetBuilderDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (widget: DashboardWidget) => void;
  widget?: DashboardWidget;
  onDelete?: (widgetId: string) => void;
}

const DRAWER_WIDTH = 420;

export default function WidgetBuilderDrawer({
  open,
  onClose,
  onSave,
  widget,
  onDelete,
}: Readonly<WidgetBuilderDrawerProps>) {
  const { customerId } = useParams();
  const isEditMode = !!widget;

  const { control, watch, setValue, reset, getValues } = useForm<WidgetBuilderFormValues>({
    defaultValues: widgetToFormValues(widget),
  });

  // Reset form whenever the drawer opens — handles both switching between
  // widgets and opening for a new widget multiple times (where widget stays undefined).
  useEffect(() => {
    if (open) {
      reset(widgetToFormValues(widget));
    }
  }, [open, widget, reset]);

  const aggregationType = watch("aggregationType");
  const chartType = watch("chartType");
  const title = watch("title");
  const field = watch("field");
  const interval = watch("interval");
  const size = watch("size");
  const filterFrom = watch("filterFrom");
  const filterTo = watch("filterTo");
  const filterAgentName = watch("filterAgentName");
  const filterDirection = watch("filterDirection");
  const filterInteractionType = watch("filterInteractionType");
  const filterSkill = watch("filterSkill");
  const filterGroup = watch("filterGroup");
  const filterTags = watch("filterTags");

  // Smart constraint enforcement: when aggregation type changes,
  // auto-switch chart type and field if current selections become invalid
  const handleAggregationTypeChange = useCallback(
    (newType: AggregationType) => {
      setValue("aggregationType", newType);

      // Auto-fix field
      const validFields = FIELDS_BY_AGGREGATION[newType];
      if (validFields.length > 0) {
        const currentFieldValid = validFields.some((f) => f.value === getValues("field"));
        if (!currentFieldValid) {
          setValue("field", validFields[0].value);
        }
      }

      // Auto-fix chart type
      const validChartTypes = CHART_TYPES_BY_AGGREGATION[newType];
      if (!validChartTypes.includes(getValues("chartType"))) {
        setValue("chartType", validChartTypes[0]);
      }
    },
    [setValue, getValues]
  );

  const allowedChartTypes = CHART_TYPES_BY_AGGREGATION[aggregationType];

  // Build filters object for preview — use individual watched fields so the
  // memo only recalculates when an actual filter value changes, not on every
  // render (watch() without args returns a new object every time).
  const previewFilters = useMemo(() => {
    const hasAny =
      filterFrom || filterTo || filterAgentName || filterDirection ||
      filterInteractionType || filterSkill || filterGroup || filterTags.length > 0;
    if (!hasAny) return undefined;
    return {
      from: filterFrom?.utc().toISOString(),
      to: filterTo?.utc().toISOString(),
      agentName: filterAgentName || undefined,
      direction: filterDirection || undefined,
      interactionType: filterInteractionType || undefined,
      skill: filterSkill || undefined,
      group: filterGroup || undefined,
      tags: filterTags.length > 0 ? filterTags : undefined,
    };
  }, [filterFrom, filterTo, filterAgentName, filterDirection, filterInteractionType, filterSkill, filterGroup, filterTags]);

  const handleAskArchie = () => {
    onClose();
    globalThis.dispatchEvent(new CustomEvent("open-archie"));
  };

  const handleSave = () => {
    const values = getValues();

    // Validation
    if (!values.title.trim()) return;
    if (values.filterFrom && values.filterTo && values.filterFrom.isAfter(values.filterTo)) return;

    const config = formValuesToConfig(values);
    const isKpi = values.chartType === "kpi";

    const saved: DashboardWidget = {
      id: widget?.id ?? uuidv4(),
      type: WIDGET_TYPES.CUSTOM_AGGREGATE,
      title: values.title,
      layout: widget?.layout ?? {
        x: 0,
        y: Infinity,
        w: isKpi ? 3 : 6,
        h: isKpi ? 2 : 4,
        minW: 2,
        minH: 2,
      },
      config,
    };
    onSave(saved);
    onClose();
  };

  const handleDelete = () => {
    if (widget && onDelete) {
      onDelete(widget.id);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">
            {isEditMode ? "Edit Widget" : "Add Widget"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Scrollable Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Basics Section */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Basics
            </Typography>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  size="small"
                  fullWidth
                  sx={{ mt: 1, mb: 2 }}
                />
              )}
            />
            <ChartTypePicker
              value={chartType}
              onChange={(v) => setValue("chartType", v)}
              allowedTypes={allowedChartTypes}
            />
          </Box>

          <Divider />

          {/* Data Section */}
          <AggregationConfig
            aggregationType={aggregationType}
            field={field}
            interval={interval}
            size={size}
            onAggregationTypeChange={handleAggregationTypeChange}
            onFieldChange={(v) => setValue("field", v)}
            onIntervalChange={(v) => setValue("interval", v)}
            onSizeChange={(v) => setValue("size", v)}
          />

          <Divider />

          {/* Filters Section */}
          {customerId && (
            <WidgetFilterSection control={control} customerId={customerId} />
          )}

          <Divider />

          {/* Live Preview */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Preview
            </Typography>
            <Box
              sx={{
                bgcolor: "background.default",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 2,
              }}
            >
              <WidgetPreview
                title={title}
                chartType={chartType}
                aggregationType={aggregationType}
                field={field}
                interval={aggregationType === "dateHistogram" ? interval : undefined}
                size={aggregationType === "terms" ? size : undefined}
                filters={previewFilters}
              />
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {isEditMode && onDelete ? (
              <Button color="error" onClick={handleDelete}>
                Delete Widget
              </Button>
            ) : (
              <Button
                startIcon={<SmartToy />}
                onClick={handleAskArchie}
                sx={{ color: "primary.main" }}
              >
                Ask Archie
              </Button>
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Save Widget
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
